// src/components/import/StarterPackCard.tsx
import React, { useState } from "react";
import { Wand2, ChevronDown, Download, FolderPlus, Upload, PlusSquare, FileText } from "lucide-react";
import { importDocxFile } from "../../services/packets-io";

type Props = {
  onOpenRoleSelect: () => void;
  onNavigateTag: () => void;
  onCreateBlank: (name: string) => Promise<string>;
  setCurrentAndRefresh: (id: string) => Promise<void>;
  showDocxExample: () => void;
};

const MORE_OPTIONS_KEY = "cueboard.import.moreOptionsOpen";

export default function StarterPackCard({
  onOpenRoleSelect,
  onNavigateTag,
  onCreateBlank,
  setCurrentAndRefresh,
  showDocxExample,
}: Props) {
  const [moreOptionsOpen, setMoreOptionsOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(MORE_OPTIONS_KEY);
      return v === null ? false : v === "1";
    } catch {
      return false;
    }
  });

  const toggleMoreOptions = () => {
    setMoreOptionsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MORE_OPTIONS_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  };

  function notifyError(e: any) {
    const msg = e?.message?.toString?.();
    alert(
      msg ||
        "We couldn't read that file. Use a Starter Pack template or paste your notes into the template and re-export."
    );
  }
  function notifyMergeSuccess(packetName: string, res: any) {
    if (res?.report) {
      const { added = 0, conflicts = 0 } = res.report;
      alert(`DOCX imported into current prep kit: "${packetName}"\nCueBoards Added: ${added} • Conflicts: ${conflicts}`);
      return;
    }
    if (res?.summary) {
      const { notes = 0, triggersKept = 0, triggersDropped = 0 } = res.summary;
      alert(
        `DOCX imported into current prep kit: "${packetName}"\n` +
          `CueBoards Added: ${notes} • Triggers Kept: ${triggersKept} • Dropped: ${triggersDropped}`
      );
      return;
    }
    alert(`DOCX imported into current: "${packetName}"`);
  }
  function notifyNewSuccess(packetName: string, res: any) {
    if (res?.summary) {
      const { notes = 0, triggersKept = 0 } = res.summary;
      alert(`New Prep Kit created from DOCX: "${packetName}"\nCueBoards Added: ${notes} • Triggers Ready: ${triggersKept}`);
      return;
    }
    alert(`New Prep Kit created from DOCX: "${packetName}"`);
  }

  // DOCX → Merge into current (autoTriggers ON)
  async function onImportDocxMerge(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { packetId, res } = await importDocxFile(f, { merge: true, autoTriggers: true });
      await setCurrentAndRefresh(packetId);
      notifyMergeSuccess(res.packet?.name || "(current)", res);
      onNavigateTag();
    } catch (err: any) {
      notifyError(err);
    } finally {
      e.target.value = "";
    }
  }

  // DOCX → Import as NEW (autoTriggers ON)
  async function onImportDocxNew(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { packetId, res } = await importDocxFile(f, { merge: false, autoTriggers: true });
      await setCurrentAndRefresh(packetId);
      const filenameBase = f.name.replace(/\.[^/.]+$/, "");
      notifyNewSuccess(res.packet?.name || filenameBase, res);
      onNavigateTag();
    } catch (err: any) {
      notifyError(err);
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="card card-accent hoverable vstack-md">
      <button className="button button-primary" onClick={onOpenRoleSelect}>
        <Wand2 size={16} className="mr-2" /> Create from Starter Pack
      </button>

      <div className="templates-strip">
        <span className="flex items-center gap-1 font-semibold">
          <Download size={14} aria-hidden="true" />
          <a href="/import-examples/general-starter-pack.docx" download>
            Download General Starter Pack (DOCX)
          </a>
        </span>
        <span className="muted" aria-hidden="true">·</span>
        <span className="muted">Or pick a role:</span>
        <a href="/import-examples/ae-starter-pack.docx" download>Account Executive</a>
        <span className="muted" aria-hidden="true">•</span>
        <a href="/import-examples/csm-starter-pack.docx" download>Customer Success Manager</a>
        <span className="muted" aria-hidden="true">•</span>
        <a href="/import-examples/pm-starter-pack.docx" download>Product Manager</a>
        <span className="muted" aria-hidden="true">•</span>
        <a href="/import-examples/swe-starter-pack.docx" download>Software Engineer</a>
        <span className="muted" aria-hidden="true">•</span>
        <a href="/import-examples/marketing-starter-pack.docx" download>Marketing</a>
      </div>

      <div style={{ marginTop: 16, position: "relative" }}>
        <button
          type="button"
          className="button button-secondary"
          onClick={toggleMoreOptions}
          aria-expanded={moreOptionsOpen}
          aria-controls="more-options-row"
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <ChevronDown size={16} className={`chevron ${moreOptionsOpen ? "expanded" : ""}`} />
          More Options
        </button>

        <div id="more-options-row" style={{ marginTop: 12, display: moreOptionsOpen ? "block" : "none" }}>
          <div className="subtle-divider" />
          <div className="inline-options">
            <button
              className="button-ghost"
              onClick={async () => {
                const name = prompt("Prep Kit name?", "My Prep Kit");
                if (!name) return;
                await onCreateBlank(name);
                onNavigateTag();
              }}
            >
              <FolderPlus size={16} className="mr-2" /> Create Blank Prep Kit
            </button>

            {/* DOCX: Import into current */}
            <button
              type="button"
              className="button-ghost"
              onClick={() => document.getElementById("import-docx-merge")?.click()}
              aria-controls="import-docx-merge"
              data-no-rowselect
            >
              <Upload size={16} className="mr-2" /> Import DOCX into Current Prep Kit
            </button>
            <input
              id="import-docx-merge"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onImportDocxMerge}
              hidden
              data-no-rowselect
            />

            {/* DOCX: Import as new */}
            <button
              type="button"
              className="button-ghost"
              onClick={() => document.getElementById("import-docx-new")?.click()}
              aria-controls="import-docx-new"
              data-no-rowselect
            >
              <PlusSquare size={16} className="mr-2" /> Import DOCX as New Prep Kit
            </button>
            <input
              id="import-docx-new"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={onImportDocxNew}
              hidden
              data-no-rowselect
            />

            <button type="button" className="button-ghost" onClick={showDocxExample}>
              <FileText size={16} className="mr-2" /> See DOCX example
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
